<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Parámetros legales</h1>
    </div>

    <wz-card :body-padding="false">
      <wz-tabs :items="tabItems" v-model="activeTab" variant="underline" style="padding: 0 20px;">

        <!-- ── 1. Valores generales ─────────────────────────────────────────────────────────────── -->
        <template #GENERAL>
          <div class="lp-tab">
            <p class="text-muted text-sm mb-3">
              Cada parámetro tiene vigencia por rango de fechas. Las vigencias cerradas se ven en gris y no se
              editan; solo se edita una vigencia sin cálculos asociados. Para cambiar un valor desde una fecha, use
              <strong>Nueva vigencia</strong>.
            </p>
            <lp-series-table :definitions="byGroup.GENERAL" :today="today"
              @new="openNew" @edit="openEdit" />
          </div>
        </template>

        <!-- ── 2. Coeficientes por régimen ──────────────────────────────────────────────────────── -->
        <template #REGIME>
          <div class="lp-tab">
            <wz-alert v-for="d in regimeWarnings" :key="d.code" variant="warning" icon="ri-error-warning-line" class="mb-2">
              <strong>{{ d.label }}:</strong> {{ d.warning }}
            </wz-alert>

            <p class="text-muted text-sm mb-2">
              Valores vigentes hoy por régimen (TUO D.S. 013-2013-PRODUCE, arts. 50, 55 y 56). Para verificarlos de un
              vistazo contra la norma.
            </p>
            <div class="overflow-x-auto mb-4">
              <table class="wz-table w-full">
                <thead>
                  <tr>
                    <th>Coeficiente</th>
                    <th v-for="r in gridRegimes" :key="r.key" style="text-align: right;">{{ r.label }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in byGroup.REGIME" :key="d.code">
                    <td>
                      {{ d.label }}
                      <i v-if="d.warning" class="ri-error-warning-line text-warning ml-1" title="Sin norma clara"></i>
                    </td>
                    <td v-for="r in gridRegimes" :key="r.key" style="text-align: right;">
                      {{ gridCell(d, r.key) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 class="font-medium mb-2">Vigencias</h3>
            <lp-series-table :definitions="byGroup.REGIME" :today="today" show-regime
              @new="openNew" @edit="openEdit" />
          </div>
        </template>

        <!-- ── 3. Tasas de seguros ──────────────────────────────────────────────────────────────── -->
        <template #INSURANCE>
          <div class="lp-tab">
            <p class="text-muted text-sm mb-3">
              Las tasas de SCTR no están fijadas por ley: cada aseguradora cotiza. Por eso se registran por empresa.
            </p>
            <div class="mb-3" style="max-width: 360px;">
              <label class="font-medium">Empresa</label>
              <wz-select :items="companyOptions" v-model="companyId" defaultOption="Seleccionar empresa"
                @change="loadData" />
            </div>
            <lp-series-table :definitions="insuranceDefinitions" :today="today" :company-id="companyId"
              @new="openNew" @edit="openEdit" />
          </div>
        </template>

      </wz-tabs>
    </wz-card>

    <legal-parameter-vigencia-modal
      v-model="modal.open"
      :definition="modal.definition"
      :row="modal.row"
      :preset-regime="modal.regime"
      :company-id="modal.companyId"
      :company-name="selectedCompanyName"
      @saved="loadData"
    />
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { LegalParameterService, CompanyService } from '@/core/services'
import LegalParameterVigenciaModal from '../Components/LegalParameterVigenciaModal.vue'
import LpSeriesTable from '../Components/LegalParameterSeriesTable.vue'
import { formatParameterValue } from '../utils/format'

const todayIso = () => {
  const d = new Date()
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'LegalParameterList',
  components: { LegalParameterVigenciaModal, LpSeriesTable },
  data() {
    return {
      activeTab: 'GENERAL',
      definitions: [],
      companies: [],
      companyId: null,
      today: todayIso(),
      gridRegimes: [
        { key: 'GENERAL', label: 'General' },
        { key: 'MYPE_PEQUENA', label: 'MYPE pequeña' },
        { key: 'MYPE_MICRO', label: 'MYPE micro' },
      ],
      modal: { open: false, definition: null, row: null, regime: null, companyId: null },
    }
  },
  computed: {
    tabItems() {
      return [
        { key: 'GENERAL', label: 'Valores generales', icon: 'ri-scales-3-line' },
        { key: 'REGIME', label: 'Coeficientes por régimen', icon: 'ri-building-2-line' },
        { key: 'INSURANCE', label: 'Tasas de seguros', icon: 'ri-shield-check-line' },
      ]
    },
    byGroup() {
      const g = { GENERAL: [], REGIME: [], INSURANCE: [] }
      this.definitions.forEach(d => { (g[d.group] || (g[d.group] = [])).push(d) })
      return g
    },
    regimeWarnings() { return this.byGroup.REGIME.filter(d => d.warning) },
    insuranceDefinitions() {
      // Las tasas por empresa solo se muestran con una empresa seleccionada; el tope global, siempre.
      return this.byGroup.INSURANCE.filter(d => d.scope !== 'COMPANY' || this.companyId)
    },
    companyOptions() { return this.companies.map(c => ({ key: c.id, label: c.name })) },
    selectedCompanyName() {
      const c = this.companies.find(x => x.id === this.companyId)
      return c ? c.name : null
    },
  },
  methods: {
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await LegalParameterService.list(this.companyId || undefined)
        this.definitions = resp.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los parámetros legales')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async loadCompanies() {
      try {
        const resp = await CompanyService.listActive()
        this.companies = resp.payload || []
      } catch (e) {
        console.error(e)
      }
    },
    currentValue(d, regime) {
      const v = (d.values || []).find(x => (x.laborRegime || null) === regime
        && x.validFrom <= this.today && (!x.validTo || x.validTo >= this.today))
      return v ? v.value : null
    },
    gridCell(d, regime) {
      const v = this.currentValue(d, regime)
      if (v === null) {
        if (regime === 'GENERAL' && d.code === 'CTS_DAILY_WAGES_PER_YEAR') return 'Fórmula general'
        if (regime === 'GENERAL' && d.code === 'CTS_DAILY_WAGES_CAP') return 'Sin tope'
        return '—'
      }
      return formatParameterValue(v, d.unit)
    },
    openNew({ definition, regime }) {
      if (definition.scope === 'COMPANY' && !this.companyId) {
        Alert.error('Seleccione una empresa')
        return
      }
      this.modal = { open: true, definition, row: null, regime: regime || null, companyId: this.companyId }
    },
    openEdit({ definition, row }) {
      this.modal = { open: true, definition, row, regime: row.laborRegime, companyId: row.companyId }
    },
  },
  async created() {
    await Promise.all([this.loadData(), this.loadCompanies()])
  },
}
</script>

<style lang="stylus">
.lp-tab
  padding 16px 20px 20px
</style>
